import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useEmpFeatureFlag } from '@/hooks/useEmpFeatureFlag';
import { usePortfolioDashboard } from '@/hooks/usePortfolioDashboard';
import MetricTooltip, { METRIC_EXPLANATIONS } from '@/components/system/MetricTooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Bell,
  Activity,
  BarChart2,
  Target,
  ChevronRight,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import type { PortfolioContractRow, PortfolioTrendPoint } from '@/hooks/usePortfolioDashboard';
import ClickableKpiCard from '@/components/system/ClickableKpiCard';
import { DataProvenanceBadge } from '@/components/system/DataProvenanceBadge';

// ─── Scope Bar ──────────────────────────────────────────────────────────────

interface PortfolioScopeBarProps {
  selectedCluster: string;
  onChangeCluster: (v: string) => void;
  activeClusterIds: string[];
}

const PortfolioScopeBar: React.FC<PortfolioScopeBarProps> = ({
  selectedCluster,
  onChangeCluster,
  activeClusterIds,
}) => {
  const { data: clusters = [] } = useQuery({
    queryKey: ['clusters-for-scope', activeClusterIds],
    queryFn: async () => {
      const { data } = await supabase
        .from('clusters')
        .select('id, name')
        .in('id', activeClusterIds);
      return data || [];
    },
    enabled: activeClusterIds.length > 0,
    staleTime: 10 * 60 * 1000,
  });

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <span className="text-sm text-muted-foreground font-medium">Cluster:</span>
      <Select value={selectedCluster} onValueChange={onChangeCluster}>
        <SelectTrigger className="w-48 h-8 text-sm border-border/60">
          <SelectValue placeholder="Todos os clusters" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">Todos os clusters</SelectItem>
          {clusters.map(c => (
            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

// ─── Semaphore helpers ─────────────────────────────────────────────────────

type KpiVariant = 'default' | 'success' | 'warning' | 'destructive';

const spiVariant = (v: number | null): KpiVariant => {
  if (v === null) return 'default';
  if (v >= 0.95) return 'success';
  if (v >= 0.80) return 'warning';
  return 'destructive';
};

const cpiVariant = (v: number | null): KpiVariant => {
  if (v === null) return 'default';
  if (v >= 0.95) return 'success';
  if (v >= 0.80) return 'warning';
  return 'destructive';
};

const countVariant = (v: number, critAt: number): KpiVariant => {
  if (v === 0) return 'success';
  if (v < critAt) return 'warning';
  return 'destructive';
};

const fmtIndex = (v: number | null) => v !== null ? v.toFixed(2) : '—';
const fmtPct = (v: number | null) => v !== null ? `${Math.round(v)}%` : '—';

// ─── Ranking Table ────────────────────────────────────────────────────────────

const criticidadeConfig = {
  critico: { label: 'Crítico', className: 'bg-destructive/10 text-destructive border-destructive/20' },
  alerta:  { label: 'Alerta',  className: 'bg-warning/10 text-warning border-warning/20' },
  ok:      { label: 'OK',      className: 'bg-success/10 text-success border-success/20' },
};

const semaphoreColors: Record<string, string> = {
  success: 'text-success',
  warning: 'text-warning',
  destructive: 'text-destructive',
  default: 'text-muted-foreground',
};

const IndexBadge: React.FC<{ value: number | null }> = ({ value }) => {
  if (value === null) return <span className="text-muted-foreground text-sm">—</span>;
  const s = spiVariant(value);
  return (
    <span className={`font-mono text-sm font-medium ${semaphoreColors[s]}`}>
      {value.toFixed(2)}
    </span>
  );
};

const PortfolioContractRanking: React.FC<{ rows: PortfolioContractRow[] }> = ({ rows }) => {
  const nav = useNavigate();
  const navigateToContract = (id: string) => nav(`/empreendimentos/contratos/${id}`);
  
  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
        Nenhum contrato encontrado no escopo selecionado
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border/40">
            <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Contrato</th>
            <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">SPI</th>
            <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">CPI</th>
            <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Físico</th>
            <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Desvios</th>
            <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Alertas</th>
            <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
            <th className="w-8"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/20">
          {rows.map(row => {
            const cfg = criticidadeConfig[row.criticidade];
            return (
              <tr key={row.id} className="hover:bg-muted/30 transition-colors cursor-pointer group" onClick={() => navigateToContract(row.id)} title="Clique para ver detalhes do contrato">
                <td className="py-2.5 px-3">
                  <span className="font-medium text-foreground truncate max-w-[180px] block" title={row.nome}>
                    {row.nome.length > 28 ? row.nome.slice(0, 26) + '…' : row.nome}
                  </span>
                </td>
                <td className="py-2.5 px-3 text-center"><IndexBadge value={row.spi} /></td>
                <td className="py-2.5 px-3 text-center"><IndexBadge value={row.cpi} /></td>
                <td className="py-2.5 px-3 text-center">
                  <span className="text-sm text-foreground">
                    {row.progressoRealizado !== null ? `${Math.round(row.progressoRealizado)}%` : '—'}
                  </span>
                </td>
                <td className="py-2.5 px-3 text-center">
                  <span className={`text-sm font-medium ${row.desvios >= 3 ? 'text-destructive' : row.desvios >= 1 ? 'text-warning' : 'text-muted-foreground'}`}>
                    {row.desvios}
                  </span>
                </td>
                <td className="py-2.5 px-3 text-center">
                  <span className={`text-sm font-medium ${row.alertas >= 2 ? 'text-warning' : 'text-muted-foreground'}`}>
                    {row.alertas}
                  </span>
                </td>
                <td className="py-2.5 px-3 text-center">
                  <Badge variant="outline" className={`text-xs ${cfg.className}`}>
                    {cfg.label}
                  </Badge>
                </td>
                <td className="py-2.5 px-3 text-right w-8">
                  <ChevronRight className="h-4 w-4 text-muted-foreground opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── Drill-Down Dialog ────────────────────────────────────────────────────────

type DrillDownType = 'spi' | 'cpi' | 'progresso' | 'desvios' | 'alertas' | 'periodo' | null;

const drillDownTitle: Record<Exclude<DrillDownType, null>, string> = {
  spi: 'Contratos por SPI',
  cpi: 'Contratos por CPI',
  progresso: 'Progresso Físico por Contrato',
  desvios: 'Contratos com Desvios Abertos',
  alertas: 'Contratos com Alertas Críticos',
  periodo: 'Contratos no Período',
};

interface DrillDownDialogProps {
  type: DrillDownType;
  rows: PortfolioContractRow[];
  periodoLabel?: string;
  onClose: () => void;
}

const DrillDownDialog: React.FC<DrillDownDialogProps> = ({ type, rows, periodoLabel, onClose }) => {
  const nav = useNavigate();
  if (!type) return null;

  const filtered = (() => {
    switch (type) {
      case 'spi':
        return [...rows].sort((a, b) => (a.spi ?? 999) - (b.spi ?? 999));
      case 'cpi':
        return [...rows].sort((a, b) => (a.cpi ?? 999) - (b.cpi ?? 999));
      case 'progresso':
        return [...rows].sort((a, b) => (a.progressoRealizado ?? 0) - (b.progressoRealizado ?? 0));
      case 'desvios':
        return rows.filter(r => r.desvios > 0).sort((a, b) => b.desvios - a.desvios);
      case 'alertas':
        return rows.filter(r => r.alertas > 0).sort((a, b) => b.alertas - a.alertas);
      case 'periodo':
        return rows;
      default:
        return rows;
    }
  })();

  return (
    <Dialog open={!!type} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="text-base">
            {drillDownTitle[type]}
            {periodoLabel && <span className="text-muted-foreground font-normal ml-2">({periodoLabel})</span>}
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/40">
                <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Contrato</th>
                {(type === 'spi' || type === 'periodo') && <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">SPI</th>}
                {(type === 'cpi' || type === 'periodo') && <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">CPI</th>}
                {type === 'progresso' && (
                  <>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Realizado</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Previsto</th>
                  </>
                )}
                {type === 'desvios' && <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Desvios</th>}
                {type === 'alertas' && <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Alertas</th>}
                <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/20">
              {filtered.map(row => {
                const cfg = criticidadeConfig[row.criticidade];
                return (
                  <tr key={row.id} className="hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => nav(`/empreendimentos/contratos/${row.id}`)}>
                    <td className="py-2 px-3 font-medium truncate max-w-[200px]">{row.nome}</td>
                    {(type === 'spi' || type === 'periodo') && <td className="py-2 px-3 text-center"><IndexBadge value={row.spi} /></td>}
                    {(type === 'cpi' || type === 'periodo') && <td className="py-2 px-3 text-center"><IndexBadge value={row.cpi} /></td>}
                    {type === 'progresso' && (
                      <>
                        <td className="py-2 px-3 text-center text-sm">{fmtPct(row.progressoRealizado)}</td>
                        <td className="py-2 px-3 text-center text-sm text-muted-foreground">{fmtPct(row.progressoPrevisto)}</td>
                      </>
                    )}
                    {type === 'desvios' && <td className="py-2 px-3 text-center text-sm font-medium text-destructive">{row.desvios}</td>}
                    {type === 'alertas' && <td className="py-2 px-3 text-center text-sm font-medium text-warning">{row.alertas}</td>}
                    <td className="py-2 px-3 text-center">
                      <Badge variant="outline" className={`text-xs ${cfg.className}`}>{cfg.label}</Badge>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="py-8 text-center text-sm text-muted-foreground">Nenhum contrato nesta categoria</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Trend Chart ─────────────────────────────────────────────────────────────

interface TrendChartProps {
  data: PortfolioTrendPoint[];
  onPointClick?: (periodo: string) => void;
}

const PortfolioTrendChart: React.FC<TrendChartProps> = ({ data, onPointClick }) => {
  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
        Sem dados de indicadores para o período
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={data}
        margin={{ left: 0, right: 16, top: 4, bottom: 0 }}
        onClick={(e) => {
          if (e?.activePayload?.[0]?.payload?.periodo && onPointClick) {
            onPointClick(e.activePayload[0].payload.periodo);
          }
        }}
        style={{ cursor: onPointClick ? 'pointer' : 'default' }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} />
        <XAxis
          dataKey="periodo"
          tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          domain={[0, 1.4]}
          tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
          tickLine={false}
          axisLine={false}
          tickFormatter={v => v.toFixed(1)}
        />
        <Tooltip
          contentStyle={{
            borderRadius: 'var(--chart-tooltip-radius)',
            border: 'none',
            boxShadow: 'var(--chart-tooltip-shadow)',
            fontSize: 12,
          }}
          formatter={(v: number) => v.toFixed(2)}
        />
        <Legend
          iconType="circle"
          iconSize={8}
          wrapperStyle={{ fontSize: '12px', paddingTop: '4px' }}
        />
        <ReferenceLine y={1} stroke="hsl(var(--border))" strokeDasharray="6 3" label={{ value: '1.0', fontSize: 12, fill: 'hsl(var(--muted-foreground))', position: 'right' }} />
        <Line
          type="monotone"
          dataKey="spi"
          name="SPI"
          stroke="hsl(var(--success))"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4 }}
        />
        <Line
          type="monotone"
          dataKey="cpi"
          name="CPI"
          stroke="hsl(var(--warning))"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const PortfolioDashboardPage: React.FC = () => {
  const { role } = useAuth();
  const { activeClusterIds } = useEmpFeatureFlag();
  const [selectedCluster, setSelectedCluster] = useState<string>('__all__');
  const [drillDown, setDrillDown] = useState<DrillDownType>(null);
  const [drillPeriodo, setDrillPeriodo] = useState<string | null>(null);

  const scopedIds = selectedCluster === '__all__' ? [] : [selectedCluster];
  const { isLoading, kpis, ranking, trend, contratos } = usePortfolioDashboard(scopedIds);

  // Guard: only gestor/admin
  if (!['admin', 'coadmin', 'admin_cluster', 'gestor', 'gestor_planejamento', 'gestor_qualidade', 'gestor_execucao', 'observador'].includes(role || '')) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        Acesso restrito a gestores e administradores.
      </div>
    );
  }

  const handleTrendClick = (periodo: string) => {
    setDrillPeriodo(periodo);
    setDrillDown('periodo');
  };

  return (
    <div className="h-full flex flex-col gap-3 overflow-auto">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 flex-shrink-0">
        <div>
          <h2 className="text-base font-semibold text-foreground">Painel Executivo</h2>
          <p className="text-[12px] text-muted-foreground mt-0.5">
            Visão consolidada · {contratos.length} contrato{contratos.length !== 1 ? 's' : ''} no escopo
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Link
            to="/empreendimentos/indicadores"
            className="text-xs text-primary hover:text-primary/80 font-medium transition-colors flex items-center gap-1"
          >
            <BarChart2 className="h-3.5 w-3.5" />
            Indicadores por Contrato
          </Link>
          <PortfolioScopeBar
            selectedCluster={selectedCluster}
            onChangeCluster={setSelectedCluster}
            activeClusterIds={activeClusterIds}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="emp-stat-card"><Skeleton className="h-20" /></div>
            ))}
          </div>
          <div className="emp-card"><Skeleton className="h-64" /></div>
          <div className="emp-card"><Skeleton className="h-48" /></div>
        </div>
      ) : (
        <>
          {/* Provenance signal — discreet indicator that all KPIs below are derived from a certified source */}
          <div className="flex justify-end -mt-1 mb-2">
            <DataProvenanceBadge variant="compact" />
          </div>
          {/* KPI Grid — Clickable */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 flex-shrink-0">
            <ClickableKpiCard
              icon={Activity}
              label="SPI Médio"
              value={fmtIndex(kpis.spiMedio)}
              variant={spiVariant(kpis.spiMedio)}
              tooltip={METRIC_EXPLANATIONS.spi}
              onClick={() => setDrillDown('spi')}
              actionHint="Ver contratos"
            />
            <ClickableKpiCard
              icon={BarChart2}
              label="CPI Médio"
              value={fmtIndex(kpis.cpiMedio)}
              variant={cpiVariant(kpis.cpiMedio)}
              tooltip={METRIC_EXPLANATIONS.cpi}
              onClick={() => setDrillDown('cpi')}
              actionHint="Ver contratos"
            />
            <ClickableKpiCard
              icon={Target}
              label="Progresso Físico"
              value={fmtPct(kpis.progressoFisico)}
              variant={
                kpis.progressoFisico === null ? 'default'
                : kpis.progressoPrevisto !== null && kpis.progressoFisico >= kpis.progressoPrevisto * 0.95 ? 'success'
                : kpis.progressoPrevisto !== null && kpis.progressoFisico >= kpis.progressoPrevisto * 0.80 ? 'warning'
                : 'destructive'
              }
              onClick={() => setDrillDown('progresso')}
              actionHint="Ver contratos"
            />
            <ClickableKpiCard
              icon={AlertTriangle}
              label="Desvios Abertos"
              value={String(kpis.desviosAbertos)}
              variant={countVariant(kpis.desviosAbertos, 5)}
              onClick={() => setDrillDown('desvios')}
              actionHint="Ver contratos"
            />
            <ClickableKpiCard
              icon={Bell}
              label="Alertas Críticos"
              value={String(kpis.alertasCriticos)}
              variant={countVariant(kpis.alertasCriticos, 5)}
              onClick={() => setDrillDown('alertas')}
              actionHint="Ver contratos"
            />
          </div>

          {/* Trend + Ranking */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 flex-1 min-h-0">
            {/* Trend chart */}
            <div className="emp-card p-4 flex flex-col min-h-0" style={{ minHeight: 240 }}>
              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                <h3 className="emp-section-title">Evolução SPI / CPI</h3>
                <span className="text-xs text-muted-foreground/60">Clique em um ponto para detalhar</span>
              </div>
              <div className="flex-1 min-h-0" style={{ minHeight: 180 }}>
                <PortfolioTrendChart data={trend} onPointClick={handleTrendClick} />
              </div>
            </div>

            {/* Ranking */}
            <div className="emp-card p-4 flex flex-col min-h-0" style={{ minHeight: 240 }}>
              <h3 className="emp-section-title mb-2 flex-shrink-0">Ranking de Contratos</h3>
              <div className="flex-1 min-h-0 overflow-auto">
                <PortfolioContractRanking rows={ranking} />
              </div>
            </div>
          </div>

          {/* Drill-down Dialog */}
          <DrillDownDialog
            type={drillDown}
            rows={ranking}
            periodoLabel={drillPeriodo || undefined}
            onClose={() => { setDrillDown(null); setDrillPeriodo(null); }}
          />
        </>
      )}
    </div>
  );
};

export default PortfolioDashboardPage;
